import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import {
  HiMenu, HiX, HiSearch, HiUser, HiShoppingBag,
  HiChevronDown, HiHome, HiShoppingCart, HiUserCircle,
  HiClipboardList, HiShieldCheck, HiLogout,
} from 'react-icons/hi';

export default function Navbar() {
  const { user, logout } = useAuth();
  const { cartItems } = useCart();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const searchInputRef = useRef(null);
  const userMenuRef = useRef(null);
  const navigate = useNavigate();

  const isHome = location.pathname === '/' || location.pathname === '/password-access';
  const isAdmin = user?.role === 'ADMIN';
  const itemCount = cartItems?.length || 0;

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
    setUserMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (searchOpen && searchInputRef.current) searchInputRef.current.focus();
  }, [searchOpen]);

  const handleLogout = () => {
    logout();
    navigate('/');
    setUserMenuOpen(false);
    setMenuOpen(false);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchOpen(false);
      setSearchQuery('');
    }
  };

  return (
    <>
      {/* ── Topbar ───────────────────────────────────────── */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isHome ? 'bg-transparent' : 'bg-white border-b border-gray-100 shadow-sm'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative flex items-center justify-between h-16 lg:h-20">

            {/* Left — hamburger + desktop nav */}
            <div className="flex items-center gap-3 lg:gap-8">
              <button
                onClick={() => setMenuOpen(true)}
                className={`lg:hidden p-1 transition-colors ${
                  isHome ? 'text-white' : 'text-gray-600 hover:text-black'
                }`}
                aria-label="Open menu"
              >
                <HiMenu size={24} />
              </button>

              <div className="hidden lg:flex items-center gap-1">
                <DesktopNavLink to="/" isHome={isHome}>Home</DesktopNavLink>
                <DesktopNavLink to="/products" isHome={isHome}>Shop</DesktopNavLink>
                {isAdmin && (
                  <Link
                    to="/admin"
                    className="ml-2 text-xs font-semibold uppercase tracking-widest bg-black text-white px-4 py-1.5 rounded-full hover:bg-gray-800 transition"
                  >
                    Admin
                  </Link>
                )}
              </div>
            </div>

            {/* Centre — logo */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <Link
                to="/"
                className="pointer-events-auto select-none whitespace-nowrap transition-opacity duration-300 hover:opacity-80"
                style={{
                  fontFamily: '"Great Vibes", cursive',
                  fontSize: 'clamp(2.2rem, 5vw, 3.2rem)',
                  fontWeight: 400,
                  color: isHome ? '#ffffff' : '#111827',
                  letterSpacing: '0.08em',
                  textShadow: isHome
                    ? '0 2px 8px rgba(0,0,0,0.25), 0 1px 2px rgba(0,0,0,0.15)'
                    : '0 1px 3px rgba(0,0,0,0.08)',
                  lineHeight: 1,
                }}
              >
                Waveyyent
              </Link>
            </div>

            {/* Right — search, user, cart */}
            <div className="flex items-center gap-4 sm:gap-5">
              {searchOpen ? (
                <button
                  onClick={() => { setSearchOpen(false); setSearchQuery(''); }}
                  className={`transition-colors ${isHome ? 'text-white' : 'text-gray-600 hover:text-black'}`}
                  aria-label="Close search"
                >
                  <HiX size={20} />
                </button>
              ) : (
                <button
                  onClick={() => setSearchOpen(true)}
                  className={`transition-colors ${isHome ? 'text-white/80 hover:text-white' : 'text-gray-500 hover:text-black'}`}
                  aria-label="Search"
                >
                  <HiSearch size={20} />
                </button>
              )}

              {/* Desktop user menu */}
              <div ref={userMenuRef} className="relative hidden sm:block">
                {user ? (
                  <>
                    <button
                      onClick={() => setUserMenuOpen(!userMenuOpen)}
                      className={`flex items-center gap-1 transition-colors ${
                        isHome ? 'text-white/80 hover:text-white' : 'text-gray-500 hover:text-black'
                      }`}
                    >
                      <HiUser size={20} />
                      <HiChevronDown
                        size={14}
                        style={{ transition: 'transform 0.2s', transform: userMenuOpen ? 'rotate(180deg)' : 'none' }}
                      />
                    </button>
                    {userMenuOpen && (
                      <div className="absolute right-0 mt-3 w-60 bg-white rounded-2xl border border-gray-100 shadow-xl py-2 z-50 animate-slide-down">
                        <UserDropdown user={user} onLogout={handleLogout} onClose={() => setUserMenuOpen(false)} isAdmin={isAdmin} />
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    to="/login"
                    className={`text-xs font-semibold uppercase tracking-widest transition-colors ${
                      isHome ? 'text-white/80 hover:text-white' : 'text-gray-600 hover:text-black'
                    }`}
                  >
                    Sign in
                  </Link>
                )}
              </div>

              {/* Cart */}
              <Link
                to="/cart"
                className={`relative transition-colors ${
                  isHome ? 'text-white/80 hover:text-white' : 'text-gray-500 hover:text-black'
                }`}
              >
                <HiShoppingBag size={20} />
                {itemCount > 0 && (
                  <span className="absolute -top-2 -right-2 w-[18px] h-[18px] text-[10px] font-bold rounded-full bg-black text-white flex items-center justify-center">
                    {itemCount}
                  </span>
                )}
              </Link>
            </div>
          </div>

          {/* Search bar */}
          {searchOpen && (
            <div className="py-3 border-t border-gray-100">
              <form onSubmit={handleSearch} className="relative max-w-2xl mx-auto">
                <HiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search products…"
                  className="w-full pl-10 pr-10 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm placeholder-gray-400 focus:outline-none focus:border-black transition-colors"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                  >
                    <HiX size={15} />
                  </button>
                )}
              </form>
            </div>
          )}
        </div>
      </nav>

      {/* ── Mobile drawer ───────────────────────────────── */}
      {menuOpen && (
        <div
          className="fixed inset-0 z-[59] bg-black/40 lg:hidden"
          onClick={() => setMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      <div
        className={`fixed top-0 left-0 bottom-0 z-[60] w-80 max-w-[85vw] bg-white flex flex-col lg:hidden
          transition-transform duration-300 ease-in-out
          ${menuOpen ? 'translate-x-0' : '-translate-x-full'}`}
      >
        {/* Drawer Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <Link
            to="/"
            onClick={() => setMenuOpen(false)}
            style={{
              fontFamily: '"Great Vibes", cursive',
              fontSize: '2.6rem',
              fontWeight: 400,
              color: '#111827',
              letterSpacing: '0.06em',
              lineHeight: 1.2,
            }}
          >
            Waveyyent
          </Link>
          <button
            onClick={() => setMenuOpen(false)}
            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-500 hover:text-black transition-colors"
            aria-label="Close menu"
          >
            <HiX size={20} />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto">
          {user && (
            <div className="mx-4 mt-4 mb-2 flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3.5">
              <div className="w-10 h-10 rounded-lg bg-black flex items-center justify-center text-white text-base font-bold flex-shrink-0">
                {(user?.name ?? user?.email ?? 'U')[0].toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-black truncate">
                  {user?.name || user?.email?.split('@')[0]}
                </p>
                <p className="text-xs text-gray-400 truncate">{user?.email}</p>
              </div>
            </div>
          )}

          <nav className="px-3 pt-3 pb-1">
            <p className={styles.sectionLabel}>Browse</p>
            <DrawerItem to="/" icon={<HiHome size={18} />} onClick={() => setMenuOpen(false)}>
              Home
            </DrawerItem>
            <DrawerItem to="/products" icon={<HiShoppingCart size={18} />} onClick={() => setMenuOpen(false)}>
              Shop
            </DrawerItem>
          </nav>

          {user ? (
            <>
              <div className={styles.divider} />
              <nav className="px-3 pb-1">
                <p className={styles.sectionLabel}>Account</p>
                <DrawerItem to="/orders" icon={<HiClipboardList size={18} />} onClick={() => setMenuOpen(false)}>
                  My orders
                </DrawerItem>
                <DrawerItem to="/profile" icon={<HiUserCircle size={18} />} onClick={() => setMenuOpen(false)}>
                  Profile
                </DrawerItem>
                <DrawerItem to="/cart" icon={<HiShoppingBag size={18} />} onClick={() => setMenuOpen(false)}>
                  Cart
                  {itemCount > 0 && (
                    <span className="ml-auto text-[11px] font-bold bg-black text-white px-2.5 py-0.5 rounded-full">
                      {itemCount}
                    </span>
                  )}
                </DrawerItem>
              </nav>

              {isAdmin && (
                <>
                  <div className={styles.divider} />
                  <nav className="px-3 pb-1">
                    <p className={styles.sectionLabel}>Admin</p>
                    <DrawerItem to="/admin" icon={<HiShieldCheck size={18} />} onClick={() => setMenuOpen(false)} bold>
                      Admin dashboard
                    </DrawerItem>
                  </nav>
                </>
              )}

              <div className={styles.divider} />
              <div className="px-3 pb-3">
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-3 w-full px-3 py-3 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
                >
                  <HiLogout size={18} />
                  Log out
                </button>
              </div>
            </>
          ) : (
            <>
              <div className={styles.divider} />
              <nav className="px-3 pb-3">
                <p className={styles.sectionLabel}>Account</p>
                <DrawerItem to="/login" icon={<HiUser size={18} />} onClick={() => setMenuOpen(false)}>
                  Sign in
                </DrawerItem>
                <DrawerItem to="/register" icon={<HiUserCircle size={18} />} onClick={() => setMenuOpen(false)}>
                  Create account
                </DrawerItem>
              </nav>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 border-t border-gray-100 px-5 py-5 flex justify-center gap-8">
          {['Instagram', 'Twitter', 'TikTok'].map((s) => (
            <a
              key={s}
              href={`https://${s.toLowerCase()}.com`}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-gray-400 hover:text-black transition-colors uppercase tracking-wider"
            >
              {s}
            </a>
          ))}
        </div>
      </div>
    </>
  );
}

/* ── Sub-components ─────────────────────────────────────── */

function DrawerItem({ to, icon, children, onClick, bold }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className={`flex items-center gap-3 px-3 py-3 rounded-lg text-sm transition-colors
        ${bold
          ? 'font-semibold text-black hover:bg-gray-100'
          : 'font-medium text-gray-600 hover:bg-gray-50 hover:text-black'
        }`}
    >
      <span className={bold ? 'text-black' : 'text-gray-400'}>{icon}</span>
      {children}
    </Link>
  );
}

function DesktopNavLink({ to, children, isHome }) {
  return (
    <Link
      to={to}
      className={`text-xs uppercase tracking-widest font-semibold relative group transition-colors px-3 py-1.5 rounded-md ${
        isHome ? 'text-white/70 hover:text-white' : 'text-gray-500 hover:text-black hover:bg-gray-50'
      }`}
    >
      {children}
      <span
        className={`absolute bottom-0 left-3 right-3 h-px transition-all duration-300 scale-x-0 group-hover:scale-x-100 origin-left ${
          isHome ? 'bg-white' : 'bg-black'
        }`}
      />
    </Link>
  );
}

function UserDropdown({ user, onLogout, onClose, isAdmin }) {
  return (
    <>
      <div className="px-4 py-3 border-b border-gray-50">
        <p className="text-sm font-semibold text-black truncate">
          {user?.name || user?.email?.split('@')[0] || 'User'}
        </p>
        <p className="text-xs text-gray-400 truncate mt-0.5">{user?.email}</p>
      </div>
      <div className="py-1.5">
        {[
          { to: '/orders', label: 'My orders' },
          { to: '/profile', label: 'Profile' },
        ].map(({ to, label }) => (
          <Link
            key={to}
            to={to}
            onClick={onClose}
            className="flex items-center px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-50 hover:text-black transition-colors"
          >
            {label}
          </Link>
        ))}
        {isAdmin && (
          <Link
            to="/admin"
            onClick={onClose}
            className="flex items-center px-4 py-2.5 text-sm font-semibold text-black hover:bg-gray-50 transition-colors"
          >
            Admin dashboard
          </Link>
        )}
      </div>
      <div className="border-t border-gray-50 pt-1.5 pb-1">
        <button
          onClick={onLogout}
          className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
        >
          <HiLogout size={15} />
          Log out
        </button>
      </div>
    </>
  );
}

/* Shared style strings */
const styles = {
  sectionLabel: 'text-[10px] font-semibold uppercase tracking-[0.1em] text-gray-400 px-3 pb-1 pt-2',
  divider: 'mx-4 my-2 h-px bg-gray-100',
};